import { useCurrentUser } from "../../hooks/useCurrentUser";
import { resolvePhotoUrl, handlePhotoError } from "../../utils/profilePhoto";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, Share2, ThumbsUp, MessageSquare, Send, Smile, X } from "lucide-react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FaWhatsapp, FaFacebookF, FaLinkedinIn, FaXTwitter } from "react-icons/fa6";
import { MdOutlineMail } from "react-icons/md";
import staricon from "../../assets/img/staricon.svg";
import fallbackSurveyImage from "../../assets/img/Rectangle23.jpg";
import { saveSurvey, likeOnSurvey, commentOnSurvey, likeOnComment, getSurveyComments, fetchSurveyLikes, getCommentReplies } from "../../service/surveyForMe";
import { removeSavedSurvey } from "../../service/mySurvey";
import { formatSurveyPostedDate, isSurveyExpired } from "../../utils/surveyPresentation";
import socketService from "../../service/socketService";
import likeSvg from "../../assets/img/like.svg";

const formatEndDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatStartDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const day = date.getDate();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const getAvatar = resolvePhotoUrl;

const renderCommentText = (comment, mentionName) => {
  const text = comment.display_comment || comment.comment || "";
  const match = text.match(/^@([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+)(?:\s+(.*))?$/);
  if (match) {
    const rest = match[2] || "";
    return (
      <>
        <span className="sfm-comment-mention" style={{ color: "#2563EB", fontWeight: "600", marginRight: "4px", cursor: "pointer" }}>
          @{mentionName || "Member"}
        </span>
        {rest}
      </>
    );
  }

  if (comment.reply_to_comment_id && mentionName && text) {
    return (
      <>
        <span className="sfm-comment-mention" style={{ color: "#2563EB", fontWeight: "600", marginRight: "4px", cursor: "pointer" }}>
          @{mentionName}
        </span>
        {text}
      </>
    );
  }

  return text;
};


const SurveyForMeSurveyCard = ({ survey, activeTab, now = Date.now(), onRemoveSaved, onToggleSaved }) => {
  const { avatar } = useCurrentUser();
  const navigate = useNavigate();
  // const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Completed Tab Social Action States
  const [likesCount, setLikesCount] = useState(survey.like_count ?? survey.likes_count ?? 0);
  const [liked, setLiked] = useState(survey.liked_by_me === true || survey.liked_by_me === 1 || survey.is_liked === 1);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [commentsCount, setCommentsCount] = useState(survey.comment_count ?? survey.comments_count ?? 0);
  const [showLikedByModal, setShowLikedByModal] = useState(false);
  const [activeReplyId, setActiveReplyId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [likedByUsers, setLikedByUsers] = useState([]);
  const [likesLoading, setLikesLoading] = useState(false);
  const [repliesMap, setRepliesMap] = useState({});
  const [repliesLoadingMap, setRepliesLoadingMap] = useState({});
  const [animateLikes, setAnimateLikes] = useState(false);
  const isFirstRender = useRef(true);
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [showShareModal, setShowShareModal] = useState(false);
  const expired = survey.isExpired || isSurveyExpired(survey.endDate, new Date(now));

  const shareUrl = survey.id
    ? `${window.location.origin}/take-survey/${survey.id}`
    : "";

  const handleCopyShareLink = async () => {
    if (!shareUrl) {
      toast.error("Survey link is not available.");
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Survey link copied successfully!");
    } catch (error) {
      console.error("Error copying survey link:", error);
      toast.error("Unable to copy survey link.");
    }
  };

  const openShareWindow = (url) => {
    if (!shareUrl) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const toggleCommentExpansion = (commentId) => {
    const numId = Number(commentId);
    setExpandedComments((prev) => {
      const next = new Set(prev);
      if (next.has(numId)) {
        next.delete(numId);
      } else {
        next.add(numId);
        if (!repliesMap[numId]) {
          fetchReplies(numId);
        }
      }
      return next;
    });
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setAnimateLikes(true);
    const timer = setTimeout(() => setAnimateLikes(false), 300);
    return () => clearTimeout(timer);
  }, [likesCount]);

  useEffect(() => {
    if (activeTab !== "completed") return;

    const token = localStorage.getItem("token");
    const socket = socketService.connect(token);

    if (socket) {
      socketService.joinSurvey(survey.id);

      const handleAllIncomingEvents = (eventName, ...args) => {
        console.log(`[SOCKET WILD-CARD DEBUG] Received Event: "${eventName}" with payload:`, args);
      };
      socket.onAny(handleAllIncomingEvents);

      const handleSurveyLikeUpdated = (data) => {
        console.log("[SOCKET DEBUG] survey_like_updated event received:", data, "current survey card:", survey.id);
        if (data && Number(data.survey_id) === Number(survey.id)) {
          if (typeof data.total_likes !== "undefined") {
            setLikesCount(data.total_likes);
          } else if (typeof data.like_count !== "undefined") {
            setLikesCount(data.like_count);
          } else if (typeof data.likes_count !== "undefined") {
            setLikesCount(data.likes_count);
          } else if (Array.isArray(data.likes)) {
            setLikesCount(data.likes.length);
          }
        }
      };

      const handleCommentAdded = (newComment) => {
        console.log("[SOCKET DEBUG] survey_comment_added event received:", newComment, "current survey card:", survey.id);
        if (!newComment) return;

        // If survey_id is present, validate it. If not, proceed since socket room is room-isolated.
        if (newComment.survey_id && Number(newComment.survey_id) !== Number(survey.id)) {
          console.log("[SOCKET DEBUG] Ignored comment add event due to survey_id mismatch:", newComment.survey_id, "vs card:", survey.id);
          return;
        }

        const commentId = Number(newComment.comment_id || newComment.id);
        const parentId = newComment.parent_comment_id ? Number(newComment.parent_comment_id) : null;

        if (parentId) {
          setRepliesMap((prev) => {
            const currentReplies = prev[parentId] || [];
            if (currentReplies.some((r) => Number(r.comment_id || r.id) === commentId)) {
              return prev;
            }
            return {
              ...prev,
              [parentId]: [...currentReplies, newComment]
            };
          });
        } else {
          setComments((prev) => {
            if (prev.some((c) => Number(c.comment_id || c.id) === commentId)) {
              return prev;
            }
            return [...prev, newComment];
          });
          setCommentsCount((prev) => prev + 1);
        }
      };

      const handleCommentLikeUpdated = (data) => {
        console.log("[SOCKET DEBUG] comment_like_updated event received:", data, "current survey card:", survey.id);
        if (!data) return;

        // If survey_id is present, validate it. If not, proceed since socket room is room-isolated.
        if (data.survey_id && Number(data.survey_id) !== Number(survey.id)) {
          console.log("[SOCKET DEBUG] Ignored comment like event due to survey_id mismatch:", data.survey_id, "vs card:", survey.id);
          return;
        }

        const targetCommentId = Number(data.comment_id);
        const newLikeCount = data.like_count ?? data.total_likes ?? 0;

        console.log("[SOCKET DEBUG] Updating comment:", targetCommentId, "with newLikeCount:", newLikeCount);

        setComments((prev) =>
          prev.map((c) => {
            const cid = Number(c.comment_id || c.id);
            if (cid === targetCommentId) {
              return {
                ...c,
                like_count: newLikeCount
              };
            }
            return c;
          })
        );

        setRepliesMap((prev) => {
          const updated = { ...prev };
          let changed = false;

          Object.keys(updated).forEach((parentId) => {
            const list = updated[parentId] || [];
            const index = list.findIndex((r) => Number(r.comment_id || r.id) === targetCommentId);
            if (index !== -1) {
              const newList = [...list];
              newList[index] = {
                ...newList[index],
                like_count: newLikeCount
              };
              updated[parentId] = newList;
              changed = true;
            }
          });

          return changed ? updated : prev;
        });
      };

      socket.on("survey_like_updated", handleSurveyLikeUpdated);
      socket.on("survey_comment_added", handleCommentAdded);
      socket.on("comment_like_updated", handleCommentLikeUpdated);

      return () => {
        socketService.leaveSurvey(survey.id);
        socket.offAny(handleAllIncomingEvents);
        socket.off("survey_like_updated", handleSurveyLikeUpdated);
        socket.off("survey_comment_added", handleCommentAdded);
        socket.off("comment_like_updated", handleCommentLikeUpdated);
      };
    }
  }, [survey.id, activeTab]);

  const handleStart = () => {
    if (Number(survey.participation_status) === 1 || activeTab === "completed") {
      toast.info("You have already completed this survey.");
      return;
    }
    if (isSurveyExpired(survey.endDate)) {
      toast.error("This survey has expired.");
      return;
    }
    navigate(`/take-survey/${survey.id}`, { state: { surveyMeta: survey } });
  };


  const handleSave = async () => {
    if (saving) return;
    try {
      setSaving(true);
      const response = await saveSurvey({ survey_id: survey.id });
      if (response && response.data && response.data.success) {
        // setIsSaved(true);
        toast.success(response.data.message || "Survey saved successfully!");
      } else {
        toast.error(response?.data?.message || "Failed to save survey.");
      }
    } catch (error) {
      console.error("Error saving survey:", error);
      toast.error(
        error.response?.data?.message || "An error occurred while saving the survey."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSaved = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to remove this survey from your saved list?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c72430",
      cancelButtonColor: "#858C95",
      confirmButtonText: "Yes, remove it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    if (removing) return;
    try {
      setRemoving(true);
      const response = await removeSavedSurvey(survey.id);
      if (response && response.data && response.data.success) {
        if (onRemoveSaved) onRemoveSaved(survey.id);
        toast.success("Survey removed from saved list successfully!");
      } else {
        toast.error(response?.data?.message || "Failed to remove survey.");
      }
    } catch (error) {
      console.error("Error removing saved survey:", error);
      toast.error(
        error.response?.data?.message || "An error occurred while removing the survey."
      );
    } finally {
      setRemoving(false);
    }
  };

  const handleBookmark = async () => {
    if (saving) return;

    try {
      setSaving(true);

      let response;

      if (survey.is_saved) {
        // Remove Saved API
        response = await removeSavedSurvey(survey.id);

        if (response?.data?.success) {
          onToggleSaved(survey.id, false);
          toast.success("Survey removed successfully!");
        }
      } else {
        // Save Survey API
        response = await saveSurvey({
          survey_id: survey.id,
        });

        if (response?.data?.success) {
          onToggleSaved(survey.id, true);
          toast.success("Survey saved successfully!");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };



  const handleLikeSurvey = async () => {
    const prevLiked = liked;
    const prevCount = likesCount;

    setLiked(!prevLiked);
    setLikesCount(prevLiked ? prevCount - 1 : prevCount + 1);

    try {
      const response = await likeOnSurvey({ survey_id: survey.id });
      if (!response || !response.data || !response.data.success) {
        setLiked(prevLiked);
        setLikesCount(prevCount);
        toast.error(response?.data?.message || "Failed to like survey.");
      } else {
        const likedNow = response.data.data?.liked ?? !prevLiked;
        toast.success(likedNow ? "Survey liked successfully!" : "Survey unliked successfully!");
      }
    } catch (err) {
      console.error("Error liking survey:", err);
      setLiked(prevLiked);
      setLikesCount(prevCount);
      toast.error("Something went wrong while liking the survey.");
    }
  };

  const findRootCommentId = (commentId) => {
    if (comments.some(c => (c.comment_id || c.id) === commentId)) {
      return commentId;
    }
    let foundRootId = null;

    const searchNested = (node, targetId) => {
      const nid = node.comment_id || node.id;
      if (nid === targetId) return true;
      if (node.replies && node.replies.length > 0) {
        return node.replies.some(child => searchNested(child, targetId));
      }
      return false;
    };

    Object.keys(repliesMap).forEach(rId => {
      const list = repliesMap[rId] || [];
      if (list.some(node => searchNested(node, commentId))) {
        foundRootId = Number(rId);
      }
    });

    return foundRootId;
  };

  const formatLikedAt = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    if (isNaN(diffMs)) return "";
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffMins < 60) {
      return `Liked ${Math.max(1, diffMins)} min ago`;
    } else if (diffHrs < 24) {
      return `Liked ${diffHrs} ${diffHrs === 1 ? "hour" : "hours"} ago`;
    } else {
      return `Liked on ${date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
    }
  };

  const handleOpenLikesModal = async () => {
    setShowLikedByModal(true);
    try {
      setLikesLoading(true);
      const response = await fetchSurveyLikes(survey.id);
      if (response && response.data && response.data.success) {
        setLikedByUsers(response.data.data || []);
        if (response.data.data) {
          setLikesCount(response.data.data.length);
        }
      }
    } catch (err) {
      console.error("Error fetching survey likes:", err);
      toast.error("Failed to load likes list.");
    } finally {
      setLikesLoading(false);
    }
  };

  const fetchReplies = async (commentId) => {
    try {
      setRepliesLoadingMap(prev => ({ ...prev, [commentId]: true }));
      const response = await getCommentReplies(commentId);
      if (response && response.data && response.data.success) {
        const dataArr = response.data.data || [];
        const rootItem = dataArr[0];
        let repliesList = dataArr;
        if (rootItem && Number(rootItem.comment_id || rootItem.id) === Number(commentId)) {
          repliesList = rootItem.replies || [];
        }
        setRepliesMap(prev => ({ ...prev, [commentId]: repliesList }));
      }
    } catch (err) {
      console.error(`Error fetching replies for comment ${commentId}:`, err);
    } finally {
      setRepliesLoadingMap(prev => ({ ...prev, [commentId]: false }));
    }
  };

  const fetchComments = async () => {
    try {
      setCommentsLoading(true);
      const response = await getSurveyComments(survey.id);
      if (response && response.data && response.data.success) {
        const fetchedComments = response.data.data || [];
        setComments(fetchedComments);
        if (response.data.data) {
          setCommentsCount(response.data.data.length);
        }
      }
    } catch (err) {
      console.error("Error fetching comments:", err);
      toast.error("Failed to load comments.");
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleToggleComments = () => {
    const nextShow = !showComments;
    setShowComments(nextShow);
    if (nextShow) {
      fetchComments();
    }
  };

  const handleAddComment = async (parentId = null) => {
    const text = parentId ? replyText : commentText;
    if (text.trim().length < 3 || text.trim().length > 500) {
      toast.warn("Comments and replies must be 3 to 500 characters.");
      return;
    }

    try {
      const response = await commentOnSurvey({
        survey_id: survey.id,
        comment: text,
        reply_to_comment_id: parentId
      });

      if (response && response.data && response.data.success) {
        toast.success("Comment posted successfully!");
        if (parentId) {
          setReplyText("");
          setActiveReplyId(null);
          const rootId = findRootCommentId(parentId);
          if (rootId) {
            setExpandedComments(prev => {
              const next = new Set(prev);
              next.add(rootId);
              return next;
            });
            fetchReplies(rootId);
          }
        } else {
          setCommentText("");
        }
        fetchComments();
      } else {
        toast.error(response?.data?.message || "Failed to post comment.");
      }
    } catch (err) {
      console.error("Error posting comment:", err);
      toast.error("Something went wrong while posting comment.");
    }
  };

  const handleLikeComment = async (commentId) => {
    const currentComment = comments.find((comment) => Number(comment.comment_id || comment.id) === Number(commentId));
    const currentReply = Object.values(repliesMap).flat().find((reply) => Number(reply.comment_id || reply.id) === Number(commentId));
    const wasLiked = currentComment?.liked_by_me === true || currentComment?.liked_by_me === 1 || currentComment?.is_liked === 1
      || currentReply?.liked_by_me === true || currentReply?.liked_by_me === 1 || currentReply?.is_liked === 1;
    const prevComments = [...comments];
    const updatedComments = comments.map(c => {
      const cid = c.comment_id || c.id;
      if (cid === commentId) {
        const prevLiked = c.liked_by_me === true || c.liked_by_me === 1 || c.is_liked === 1;
        return {
          ...c,
          liked_by_me: !prevLiked,
          is_liked: prevLiked ? 0 : 1,
          like_count: prevLiked ? (c.like_count || 1) - 1 : (c.like_count || 0) + 1,
          likes_count: prevLiked ? (c.likes_count || 1) - 1 : (c.likes_count || 0) + 1
        };
      }
      return c;
    });
    setComments(updatedComments);

    const prevRepliesMap = { ...repliesMap };
    setRepliesMap((prev) => {
      const updated = { ...prev };
      let changed = false;
      Object.keys(updated).forEach((parentId) => {
        const list = updated[parentId] || [];
        const index = list.findIndex((r) => (r.comment_id || r.id) === commentId);
        if (index !== -1) {
          const newList = [...list];
          const item = newList[index];
          const prevLiked = item.liked_by_me === true || item.liked_by_me === 1 || item.is_liked === 1;
          newList[index] = {
            ...item,
            liked_by_me: !prevLiked,
            is_liked: prevLiked ? 0 : 1,
            like_count: prevLiked ? (item.like_count || 1) - 1 : (item.like_count || 0) + 1,
            likes_count: prevLiked ? (item.likes_count || 1) - 1 : (item.likes_count || 0) + 1
          };
          updated[parentId] = newList;
          changed = true;
        }
      });
      return changed ? updated : prev;
    });

    try {
      const response = await likeOnComment({ comment_id: commentId });
      if (!response || !response.data || !response.data.success) {
        setComments(prevComments);
        setRepliesMap(prevRepliesMap);
        toast.error(response?.data?.message || "Failed to like comment.");
      } else {
        const likedNow = response.data.data?.liked ?? !wasLiked;
        toast.success(likedNow ? "Comment liked successfully!" : "Comment unliked successfully!");
      }
    } catch (err) {
      console.error("Error liking comment:", err);
      setComments(prevComments);
      setRepliesMap(prevRepliesMap);
      toast.error("Something went wrong while liking the comment.");
    }
  };

  const renderCommentNode = (comment, depth = 0) => {
    const commentId = Number(comment.comment_id || comment.id);
    const commentLikesCount = comment.like_count ?? comment.likes_count ?? 0;
    const commentLiked = comment.liked_by_me === true || comment.liked_by_me === 1 || comment.is_liked === 1;
    const isExpanded = expandedComments.has(commentId);
    const childReplies = depth === 0 ? (repliesMap[commentId] || []) : (comment.replies || []);
    const rawReplyCount = comment.reply_count ?? childReplies.length ?? 0;
    const mentionTarget = [...comments, ...Object.values(repliesMap).flat()].find(
      (item) => Number(item.comment_id || item.id) === Number(comment.reply_to_comment_id)
    );
    const mentionName = comment.reply_to_member_name || mentionTarget?.member_name || mentionTarget?.creator_name;

    return (
      <div key={commentId} className={depth > 0 ? "sfm-reply-item-wrapper" : ""}>
        {/* Comment Bubble Body */}
        <div className={depth > 0 ? "sfm-reply-item" : "sfm-comment-item"}>
          <img
            src={getAvatar(comment.profile_photo || comment.member_avatar || comment.avatar)} onError={handlePhotoError}
            alt=""
            className="sfm-comment-avatar"
            style={depth > 0 ? { width: "32px", height: "32px" } : undefined}
          />
          <div className="sfm-comment-content-box">
            <div>
              <span className="sfm-comment-author-name">
                {comment.creator_name || comment.member_name || comment.name || "Rahul Verma"}
              </span>
              <span className="sfm-comment-time">
                {comment.created_on
                  ? new Date(comment.created_on).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                  : comment.created_at
                    ? new Date(comment.created_at).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                    : "15 Min Ago"}
              </span>
            </div>
            <p className="sfm-comment-text">{renderCommentText(comment, mentionName)}</p>

            {/* Comment Actions */}
            <div className="sfm-comment-actions">
              <button
                type="button"
                className={`sfm-comment-action-btn ${commentLiked ? "liked" : ""}`}
                onClick={() => handleLikeComment(commentId)}
              >
                Like
              </button>
              <button
                type="button"
                className="sfm-comment-action-btn"
                onClick={() => {
                  setActiveReplyId(activeReplyId === commentId ? null : commentId);
                  setReplyText("");
                }}
              >
                Reply
              </button>
              {commentLikesCount > 0 && (
                <span className="sfm-comment-likes-badge">
                  <ThumbsUp size={10} fill="currentColor" /> {commentLikesCount}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Write Nested Sub-Reply Input (opens directly below target comment) */}
        {activeReplyId === commentId && (
          <div className="sfm-sub-reply-input-box" style={{ marginLeft: "44px", marginBottom: "12px" }}>
            <div className="sfm-write-comment-input-wrap">
              <textarea
                placeholder="Write a reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                maxLength={500}
                rows={1}
              />
              <button
                type="button"
                className="sfm-write-comment-icon-btn send-btn"
                onClick={() => handleAddComment(commentId)}
                disabled={!replyText.trim()}
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Instagram style expand button: Show if reply_count > 0 or childReplies is not empty */}
        {rawReplyCount > 0 && (
          <div className="sfm-comment-replies-section">
            {isExpanded ? (
              <>
                <div
                  className="sfm-view-replies-btn"
                  onClick={() => toggleCommentExpansion(commentId)}
                >
                  <span className="sfm-replies-line-indicator"></span>
                  Hide replies
                </div>

                <div className="sfm-comment-replies-container">
                  {repliesLoadingMap[commentId] ? (
                    <div className="sfm-reply-loader">
                      <span className="sfm-reply-spinner"></span>
                      Loading replies...
                    </div>
                  ) : (
                    childReplies.map((child) => renderCommentNode(child, depth + 1))
                  )}
                </div>
              </>
            ) : (
              <div
                className="sfm-view-replies-btn"
                onClick={() => toggleCommentExpansion(commentId)}
              >
                <span className="sfm-replies-line-indicator"></span>
                {`View ${rawReplyCount} ${rawReplyCount === 1 ? "reply" : "replies"}`}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (


    <article className={`sfm-card ${expired && activeTab === "saved" ? "expired" : ""}`}>
      {/* Author Row */}
      <div className="sfm-author-row justify-content-between w-100">
        <div className="d-flex align-items-center gap-2">
          <img src={getAvatar(survey.author.avatar)} onError={handlePhotoError} alt={survey.author.name} className="sfm-avatar" />
          <div>
            {activeTab === "available" || activeTab === "completed" ? (
              <>
                <h3>{survey.creator_name || survey.author.name}</h3>
                <p>
                  {survey.job_title || "Product Researcher"}
                  {survey.start_date && (
                    <>
                      {" "}<span>-</span> {formatStartDate(survey.start_date)}
                    </>
                  )}
                </p>
              </>
            ) : (
              <>
                <h3>{survey.creator_name || survey.author.name}</h3>
                <p>
                  {survey.job_title || survey.author.jobTitle || "Product Researcher"}
                  {survey.postedAt && <> <span>-</span> {formatSurveyPostedDate(survey.postedAt, now)}</>}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Expiration Notice on Author Row */}
        {expired && activeTab === "saved" && (
          <div className="d-flex align-items-center gap-1 fw-bold ft-12 ms-auto" style={{ color: "#c72430" }}>
            <i className="bi bi-info-circle" style={{ fontSize: "14px" }}></i>
            <span>Expired On {formatEndDate(survey.endDate)}</span>
          </div>
        )}

        {/* Completed Badge on Author Row */}
        {(activeTab === "completed" || Number(survey.participation_status) === 1) && (
          <div className="d-flex align-items-center gap-1 fw-bold ft-12 ms-auto" style={{
            backgroundColor: "#E6F4EA",
            color: "#137333",
            borderRadius: "30px",
            padding: "4px 12px",
            fontSize: "11px",
            lineHeight: "16px"
          }}>
            <i className="bi bi-check-circle-fill"></i>
            <span>Completed</span>
          </div>
        )}
      </div>

      {/* Cover Image & Reward Pill */}
      <div className="sfm-cover-wrap">
        <img src={survey.coverImage} onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = fallbackSurveyImage; }} alt={survey.title} className="sfm-cover" />
        <div className="sfm-reward-pill">
          <img src={staricon} alt="" />
          <span>Reward: {survey.rewardCoins} Coins</span>
        </div>
      </div>

      {/* Card Body */}
      <div className="sfm-card-body">
        <h2>{survey.title}</h2>
        <p className="sfm-description">{survey.description}</p>

        <div className="sfm-tags">
          {survey.tags.map((tag) => (
            <span key={tag}>{tag.startsWith("#") ? tag : `#${tag}`}</span>
          ))}
        </div>
      </div>



      {/* Card Footer Actions */}
      {activeTab !== "completed" && (
        <div className="sfm-card-footer">
          {activeTab === "saved" ? (
            <>
              <button
                type="button"
                className="btn border-0 rounded-3 d-flex align-items-center justify-content-center px-3 py-2"
                style={{
                  background: "#FFF4F4",
                  color: "#c72430",
                  borderRadius: "6px",
                  transition: "all 0.2s ease",
                }}
                onClick={handleRemoveSaved}
                disabled={removing}
              >
                <Bookmark size={15} fill="#c72430" color="#c72430" style={{ marginRight: "6px" }} />
                <span className="ft-12 fw-bold">Remove Saved</span>
              </button>

              {expired ? (
                <button
                  type="button"
                  className="fw-bold ft-14 text-white px-4 py-2 rounded-2 border-0"
                  disabled
                  style={{
                    minWidth: "118px",
                    minHeight: "40px",
                    borderRadius: "6px",
                    fontSize: "13px",
                    backgroundColor: "#b4b4b4",
                    cursor: "not-allowed",
                  }}
                >
                  Expired
                </button>
              ) : Number(survey.participation_status) === 1 ? (
                <button
                  type="button"
                  className="sfm-start-btn"
                  style={{
                    backgroundColor: "#E6F4EA",
                    color: "#137333",
                    border: "none",
                    cursor: "not-allowed",
                    pointerEvents: "none",
                  }}
                  disabled
                >
                  Completed
                </button>
              ) : (
                <button type="button" className="sfm-start-btn" onClick={handleStart}>
                  Start Survey
                </button>
              )}
            </>
          ) : (
            <>
              <div className="sfm-actions">
                <button type="button" aria-label="Share survey" onClick={() => setShowShareModal(true)}>
                  <Share2 size={15} />
                </button>

                <button
                  type="button"
                  className="sfm-save-btn"
                  aria-label="Save survey"
                  onClick={handleBookmark}
                  disabled={saving}
                >
                  <Bookmark
                    size={15}
                    fill={survey.is_saved ? "#c72430" : "none"}
                    color={survey.is_saved ? "#c72430" : "currentColor"}
                  />

                </button>

              </div>

              {expired ? (
                <button type="button" className="sfm-start-btn" disabled>Expired</button>
              ) : activeTab === "completed" || Number(survey.participation_status) === 1 ? (
                <button
                  type="button"
                  className="sfm-start-btn"
                  style={{
                    backgroundColor: "#E6F4EA",
                    color: "#137333",
                    border: "none",
                    cursor: "not-allowed",
                    pointerEvents: "none",
                  }}
                  disabled
                >
                  Completed
                </button>
              ) : (
                <button type="button" className="sfm-start-btn" onClick={handleStart}>
                  Start Survey
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Completed Social Action Section */}
      {activeTab === "completed" && (
        <>
          {/* Social Bar */}
          <div className="sfm-social-bar">
            <div className="sfm-social-actions-left">
              <button
                type="button"
                className={`sfm-social-btn ${liked ? "liked" : ""}`}
                onClick={handleLikeSurvey}
              >
                <ThumbsUp size={15} />
                <span className={animateLikes ? "sfm-likes-bounce-pop" : ""}>{likesCount} Likes</span>
              </button>
              <button
                type="button"
                className="sfm-social-btn"
                onClick={handleToggleComments}
              >
                <MessageSquare size={15} />
                <span>{commentsCount} Comment</span>
              </button>
              <button type="button" className="sfm-social-btn" onClick={() => setShowShareModal(true)}>
                <Share2 size={15} />
                <span>{survey.shares_count ?? 0} Share</span>
              </button>
            </div>
            {likesCount > 0 && (
              <div
                className="sfm-like-indicator"
                onClick={handleOpenLikesModal}
                title="View who liked this survey"
                style={{ cursor: "pointer" }}
              >
                <img src={likeSvg} alt="like badge" style={{ width: "20px", height: "20px" }} />
              </div>
            )}
          </div>

          {/* Comments Section */}
          {showComments && (
            <div className="sfm-comments-section">
              {/* Comments List */}
              <div className="sfm-comments-list">
                {commentsLoading ? (
                  /* Skeleton Loader */
                  <>
                    <div className="sfm-comment-skeleton mb-3">
                      <div className="sfm-skeleton-circle"></div>
                      <div className="sfm-skeleton-content">
                        <div className="sfm-skeleton-line short"></div>
                        <div className="sfm-skeleton-line long"></div>
                      </div>
                    </div>
                    <div className="sfm-comment-skeleton">
                      <div className="sfm-skeleton-circle"></div>
                      <div className="sfm-skeleton-content">
                        <div className="sfm-skeleton-line short"></div>
                        <div className="sfm-skeleton-line long"></div>
                      </div>
                    </div>
                  </>
                ) : comments.length > 0 ? (
                  comments
                    .filter((c) => !c.parent_comment_id)
                    .map((comment) => renderCommentNode(comment, 0))
                ) : (
                  <p className="text-muted ft-12 text-center py-2 m-0">No comments yet. Share your thoughts!</p>
                )}
              </div>

              {/* Main Comment Write Box */}
              <div className="sfm-write-comment-bar">
                <img
                  src={avatar}
                  onError={handlePhotoError}
                  alt="user"
                  className="sfm-comment-avatar"
                />
                <div className="sfm-write-comment-input-wrap">
                  <textarea
                    placeholder="Write A Comment..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    maxLength={500}
                    rows={1}
                  />
                  <button type="button" className="sfm-write-comment-icon-btn" aria-label="Choose emoji" aria-expanded={showEmojiPicker} onClick={() => setShowEmojiPicker((open) => !open)}>
                    <Smile size={16} />
                  </button>
                  {showEmojiPicker && (
                    <div className="sfm-emoji-picker" aria-label="Choose an emoji">
                      {["😀", "😊", "❤️", "👍", "🎉"].map((emoji) => (
                        <button key={emoji} type="button" aria-label={`Insert ${emoji}`} onClick={() => { setCommentText((text) => `${text}${emoji}`.slice(0, 500)); setShowEmojiPicker(false); }}>
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    className="sfm-write-comment-icon-btn send-btn"
                    onClick={() => handleAddComment(null)}
                    disabled={!commentText.trim()}
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Liked By Modal Popup */}
      {showLikedByModal && (
        <div className="liked-by-modal-overlay" onClick={() => setShowLikedByModal(false)}>
          <div className="liked-by-modal" onClick={(e) => e.stopPropagation()}>
            <div className="liked-by-modal-header">
              <h2>Liked by</h2>
              <button
                type="button"
                className="liked-by-modal-close-x"
                onClick={() => setShowLikedByModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="liked-by-modal-subheader">
              <ThumbsUp size={14} fill="currentColor" style={{ marginRight: "4px" }} />
              <span>{likesCount} Likes</span>
            </div>

            <div className="liked-by-modal-list">
              {likesLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-danger" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2 text-muted ft-13">Loading likes...</span>
                </div>
              ) : likedByUsers.length > 0 ? (
                likedByUsers.map((user, idx) => (
                  <div className="liked-by-user-row" key={idx}>
                    <div className="liked-by-user-info">
                      <img
                        src={getAvatar(user.profile_photo)} onError={handlePhotoError}
                        alt={user.full_name}
                        className="liked-by-user-avatar"
                      />
                      <div className="liked-by-user-details">
                        <h3>{user.full_name}</h3>
                        <p>
                          {user.job_title || "Member"}
                          {user.company_name && ` at ${user.company_name}`}
                        </p>
                        <div className="liked-by-timestamp">{formatLikedAt(user.liked_at)}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="liked-by-view-profile-btn"
                      onClick={() => {
                        setShowLikedByModal(false);
                        navigate(`/PeopleProfile/${user.member_id}`);
                      }}
                    >
                      View Profile
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-muted ft-13">No likes yet. Be the first!</div>
              )}
            </div>

          </div>
        </div>
      )}

      {showShareModal && (
        <div className="sfm-share-modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="sfm-share-modal" onClick={(event) => event.stopPropagation()}>
            <div className="sfm-share-modal-header">
              <h2>Share Survey</h2>
              <button type="button" onClick={() => setShowShareModal(false)} aria-label="Close share modal">
                <X size={20} />
              </button>
            </div>

            <div className="sfm-share-summary">
              <div className="sfm-share-summary-icon"><Share2 size={20} /></div>
              <div>
                <h3>{survey.title}</h3>
                <p>Share this survey with your network and get more responses.</p>
              </div>
            </div>

            <p className="sfm-share-label">Share Link</p>
            <div className="sfm-share-link-row">
              <span className="sfm-share-link-icon" aria-hidden="true">&#128279;</span>
              <input type="text" value={shareUrl || "Survey link unavailable"} readOnly aria-label="Survey share link" />
              <button type="button" onClick={handleCopyShareLink} disabled={!shareUrl}>Copy</button>
            </div>

            <div className="sfm-share-options">
              <p>Share via</p>
              <div className="sfm-share-options-grid">
                <div className="sfm-share-option"><button type="button" className="social-btn whatsapp" onClick={() => openShareWindow(`https://wa.me/?text=${encodeURIComponent(`${survey.title} ${shareUrl}`)}`)}><FaWhatsapp size={22} color="#fff" /></button><span>WhatsApp</span></div>
                <div className="sfm-share-option"><button type="button" className="social-btn facebook" onClick={() => openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`)}><FaFacebookF size={20} color="#fff" /></button><span>Facebook</span></div>
                <div className="sfm-share-option"><button type="button" className="social-btn linkedin" onClick={() => openShareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`)}><FaLinkedinIn size={20} color="#fff" /></button><span>LinkedIn</span></div>
                <div className="sfm-share-option"><button type="button" className="social-btn twitter" onClick={() => openShareWindow(`https://twitter.com/intent/tweet?text=${encodeURIComponent(survey.title)}&url=${encodeURIComponent(shareUrl)}`)}><FaXTwitter size={20} color="#fff" /></button><span>X (Twitter)</span></div>
                <div className="sfm-share-option"><a className="social-btn email" href={shareUrl ? `mailto:?subject=${encodeURIComponent(survey.title)}&body=${encodeURIComponent(shareUrl)}` : "#"} onClick={(event) => { if (!shareUrl) event.preventDefault(); }}><MdOutlineMail size={22} color="#4B5563" /></a><span>Email</span></div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Expiration Banner */}
      {expired && activeTab === "saved" && (
        <div
          className="mt-3"
          style={{
            background: "#FFF4F4",
            border: "1px solid #C72430",
            borderRadius: "6px",
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            color: "#2c2e33",
            fontSize: "13px",
            fontWeight: "500",
            fontStyle: "italic",
          }}
        >
          <i className="bi bi-info-circle me-2" style={{ color: "#C72430", fontSize: "16px", flexShrink: 0 }}></i>
          <span>This survey is no longer accepting responses.</span>
        </div>
      )}
    </article>


  );
};

export default SurveyForMeSurveyCard;
